import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AlertsStore } from '../alerts/alerts.store';
import { useToaster } from '../shared/composables/use-toaster';
import { Site, SiteId } from '../site-detail/models/Site';
import { SITE_GATEWAY } from '../site-detail/services/SiteGateway';

import { FavoritesGateway } from './favorites.gateway';
import { Favorite, RESOURCE_TYPE } from './models/favorite';
import { FavoriteSite } from './models/favorite-site';
import { buildFavoriteSites } from './utils/build-favorite-sites';

const MAX_FAVORITES = 5;
const REPORTED_ERRORS = new Set(['wrong_customerId', 'favorite_limit', 'not_supported', 'unknown_gateway']);

interface FavoritesState {
    favorites: readonly Favorite[];
    sites: Record<SiteId, Site>;
}

/**
 * The installer's favorite installations, shown on the dashboard with the severity of their alerts.
 * Loaded when first used; the state lives as long as the page: logging out leaves the application, so there is no
 * session reset.
 */
@Injectable({ providedIn: 'root' })
export class FavoritesStore {
    private readonly gateway = inject(FavoritesGateway);
    private readonly siteGateway = inject(SITE_GATEWAY);
    private readonly alerts = inject(AlertsStore);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    /** Bumped to load again while the first load runs: reload() does nothing then. */
    private readonly generation = signal(0);
    private readonly state = resource({ params: this.generation, loader: () => this.fetch() });
    private readonly favorites = computed(() => (this.state.hasValue() ? this.state.value().favorites : []));

    /** True until the first list arrives: a reload keeps the list shown. */
    readonly loading = computed(() => this.state.isLoading() && !this.state.hasValue());

    /** Empty while loading or when the request failed. */
    readonly favoriteSites = computed<FavoriteSite[]>(() => {
        if (!this.state.hasValue()) return [];

        const { favorites, sites } = this.state.value();
        return buildFavoriteSites(favorites, sites, this.alerts.pendingAlerts());
    });

    readonly isMaxReached = computed(() => this.favorites().length >= MAX_FAVORITES);

    isFavorite(siteId: SiteId): boolean {
        return this.favorites().some(({ resourceOID }) => resourceOID === siteId);
    }

    /** Asks the backend again, keeping the list shown meanwhile. Does nothing while a load is in flight. */
    reload(): void {
        this.state.reload();
    }

    /** Never rejects: a refusal is reported with a toast. */
    async add(site: Site): Promise<void> {
        try {
            const favorite = await this.gateway.add(site.id);
            this.reconcile(({ favorites, sites }) => ({
                favorites: [...favorites, favorite],
                sites: { ...sites, [site.id]: site },
            }));
        } catch (error) {
            // Added from elsewhere in the meantime: the list shown is stale.
            if (errorCode(error) === 'already_favorite') this.loadAgain();
            else this.notifyError(error);
        }
    }

    /** Never rejects: a refusal is reported with a toast. Does nothing for an installation that is not a favorite. */
    async remove(siteId: SiteId): Promise<void> {
        const favorite = (await this.knownFavorites()).find(({ resourceOID }) => resourceOID === siteId);
        if (!favorite) return;

        try {
            await this.gateway.remove(favorite.favoriteOID);
        } catch (error) {
            // Already removed from elsewhere: nothing left to undo.
            if (errorCode(error) !== 'not_favorite') {
                this.notifyError(error);
                return;
            }
        }

        this.reconcile((state) => ({
            ...state,
            favorites: state.favorites.filter(({ favoriteOID }) => favoriteOID !== favorite.favoriteOID),
        }));
    }

    private async fetch(): Promise<FavoritesState> {
        const favorites = (await this.gateway.getFavorites()).filter(
            ({ resourceType }) => resourceType === RESOURCE_TYPE.SITE,
        );
        // Without their installations the favorites are still known: the limit and the remove actions keep working.
        const sites = await firstValueFrom(
            this.siteGateway.getSitesBatch(favorites.map(({ resourceOID }) => resourceOID)),
        ).catch((): Site[] => []);

        return { favorites, sites: Object.fromEntries(sites.map((site) => [site.id, site])) };
    }

    /**
     * Applies a change the backend confirmed to the list shown. Without a list, the first load running or failed,
     * loads it again instead: the first load may answer with a list sent before the change.
     */
    private reconcile(change: (state: FavoritesState) => FavoritesState): void {
        if (this.state.hasValue()) {
            // set() cancels a reload in flight: ask again, now that the backend has the change.
            const reloading = this.state.status() === 'reloading';
            this.state.set(change(this.state.value()));
            if (reloading) this.state.reload();
        } else {
            this.loadAgain();
        }
    }

    /**
     * Asks the backend again, keeping the list shown meanwhile. Without a list, a new generation restarts a first load
     * in flight, whose answer is then ignored: there is no list for it to reset.
     */
    private loadAgain(): void {
        if (this.state.hasValue()) this.state.reload();
        else this.generation.update((generation) => generation + 1);
    }

    /** The list shown, or the backend's when there is none: an installation deleted before the load, or after it failed. */
    private async knownFavorites(): Promise<readonly Favorite[]> {
        if (this.state.hasValue()) return this.favorites();

        try {
            return await this.gateway.getFavorites();
        } catch {
            return [];
        }
    }

    private notifyError(error: unknown): void {
        const code = errorCode(error);
        const key =
            code && REPORTED_ERRORS.has(code) ? `ERROR_FAVORITE_${code.toUpperCase()}` : 'ERROR_FAVORITE_UNKNOWN';
        this.toaster.error({ title: this.translate.instant(key) });
    }
}

function errorCode(error: unknown): string | undefined {
    return error instanceof HttpErrorResponse ? error.error?.message : undefined;
}
